import React, { useState, useEffect } from "react";
import { AppLayout } from "@/components/AppLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Calendar as CalendarIcon, Plus, Trash2, Loader2, MapPin, Clock, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const typeColors = { audience: "bg-red-100 text-red-700", reunion: "bg-blue-100 text-blue-700", echeance: "bg-amber-100 text-amber-700", rappel: "bg-green-100 text-green-700" };
const typeLabels = { audience: "Audience", reunion: "Réunion", echeance: "Échéance", rappel: "Rappel" };
const DAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

function EventForm({ event, clients, cases, onSave, onClose }) {
  var [form, setForm] = useState({
    title: event?.title || "", description: event?.description || "",
    event_type: event?.event_type || "audience", date: event?.date || "",
    time: event?.time || "", location: event?.location || "",
    client_id: event?.client_id || "", case_id: event?.case_id || "",
    reminder_days: event?.reminder_days ?? 1
  });
  var [saving, setSaving] = useState(false);

  function set(field, value) { setForm(function(p) { return Object.assign({}, p, { [field]: value }); }); }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.title || !form.date) { toast.error("Titre et date requis"); return; }
    setSaving(true);
    var body = Object.assign({}, form);
    if (!body.client_id) delete body.client_id;
    if (!body.case_id) delete body.case_id;
    try {
      var url = event?.event_id ? API_URL + "/api/events/" + event.event_id : API_URL + "/api/events";
      var res = await fetch(url, {
        method: event?.event_id ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify(body)
      });
      if (res.ok) { toast.success(event?.event_id ? "Modifié" : "Créé"); onSave(); onClose(); }
      else { var err = await res.json(); toast.error(err.detail || "Erreur"); }
    } catch (err) { toast.error("Erreur"); }
    setSaving(false);
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2"><Label>Titre</Label><Input value={form.title} onChange={function(e) { set("title", e.target.value); }} data-testid="event-title" /></div>
        <div><Label>Type</Label>
          <Select value={form.event_type} onValueChange={function(v) { set("event_type", v); }}>
            <SelectTrigger data-testid="event-type"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="audience">Audience</SelectItem><SelectItem value="reunion">Réunion</SelectItem><SelectItem value="echeance">Échéance</SelectItem><SelectItem value="rappel">Rappel</SelectItem></SelectContent>
          </Select>
        </div>
        <div><Label>Date</Label><Input type="date" value={form.date} onChange={function(e) { set("date", e.target.value); }} data-testid="event-date" /></div>
        <div><Label>Heure</Label><Input type="time" value={form.time} onChange={function(e) { set("time", e.target.value); }} data-testid="event-time" /></div>
        <div><Label>Lieu</Label><Input value={form.location} onChange={function(e) { set("location", e.target.value); }} data-testid="event-location" /></div>
        <div><Label>Rappel (jours avant)</Label><Input type="number" value={form.reminder_days} onChange={function(e) { set("reminder_days", parseInt(e.target.value) || 0); }} /></div>
        <div className="col-span-2"><Label>Description</Label><Textarea value={form.description} onChange={function(e) { set("description", e.target.value); }} rows={2} /></div>
      </div>
      <DialogFooter><Button type="button" variant="outline" onClick={onClose}>Annuler</Button><Button type="submit" disabled={saving} data-testid="save-event-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Enregistrer</Button></DialogFooter>
    </form>
  );
}

export default function CalendarPage({ user, setUser }) {
  var [events, setEvents] = useState([]);
  var [loading, setLoading] = useState(true);
  var [currentDate, setCurrentDate] = useState(new Date());
  var [showForm, setShowForm] = useState(false);
  var [clients, setClients] = useState([]);
  var [cases, setCases] = useState([]);

  var year = currentDate.getFullYear();
  var month = currentDate.getMonth();
  var monthStr = year + "-" + String(month + 1).padStart(2, "0");

  useEffect(function() { fetchEvents(); }, [monthStr]);
  useEffect(function() {
    fetch(API_URL + "/api/clients", { credentials: "include" }).then(function(r) { return r.json(); }).then(setClients).catch(function() {});
    fetch(API_URL + "/api/cases", { credentials: "include" }).then(function(r) { return r.json(); }).then(setCases).catch(function() {});
  }, []);

  async function fetchEvents() {
    setLoading(true);
    try {
      var res = await fetch(API_URL + "/api/events?month=" + monthStr, { credentials: "include" });
      if (res.ok) setEvents(await res.json());
    } catch (e) { }
    setLoading(false);
  }

  async function handleDelete(eventId) {
    await fetch(API_URL + "/api/events/" + eventId, { method: "DELETE", credentials: "include" });
    toast.success("Supprimé"); fetchEvents();
  }

  function prevMonth() { setCurrentDate(new Date(year, month - 1, 1)); }
  function nextMonth() { setCurrentDate(new Date(year, month + 1, 1)); }

  var firstDay = new Date(year, month, 1).getDay();
  var daysInMonth = new Date(year, month + 1, 0).getDate();
  var offset = (firstDay + 6) % 7;

  var calendarCells = [];
  for (var i = 0; i < offset; i++) calendarCells.push(null);
  for (var d = 1; d <= daysInMonth; d++) calendarCells.push(d);

  function getEventsForDay(day) {
    var dayStr = monthStr + "-" + String(day).padStart(2, "0");
    return events.filter(function(e) { return e.date === dayStr; });
  }

  return (
    <AppLayout user={user} setUser={setUser}>
      <div className="p-8" data-testid="calendar-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-2xl font-semibold tracking-tight mb-1">Calendrier</h1>
            <p className="text-sm text-muted-foreground">Audiences, réunions et échéances</p>
          </div>
          <Dialog open={showForm} onOpenChange={setShowForm}>
            <DialogTrigger asChild>
              <Button data-testid="add-event-btn"><Plus className="w-4 h-4 mr-2" />Nouvel événement</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Nouvel événement</DialogTitle><DialogDescription>Planifiez un événement</DialogDescription></DialogHeader>
              <EventForm clients={clients} cases={cases} onSave={fetchEvents} onClose={function() { setShowForm(false); }} />
            </DialogContent>
          </Dialog>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <Button variant="ghost" size="icon" onClick={prevMonth} data-testid="prev-month"><ChevronLeft className="w-4 h-4" /></Button>
              <CardTitle className="text-lg">{MONTHS[month]} {year}</CardTitle>
              <Button variant="ghost" size="icon" onClick={nextMonth} data-testid="next-month"><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-7 gap-px bg-slate-200 rounded-lg overflow-hidden">
              {DAYS.map(function(d) { return <div key={d} className="bg-slate-50 p-2 text-center text-xs font-medium text-muted-foreground">{d}</div>; })}
              {calendarCells.map(function(day, idx) {
                if (day === null) return <div key={"e" + idx} className="bg-white p-2 min-h-[80px]" />;
                var dayEvents = getEventsForDay(day);
                var isToday = day === new Date().getDate() && month === new Date().getMonth() && year === new Date().getFullYear();
                return (
                  <div key={day} className={"bg-white p-2 min-h-[80px] " + (isToday ? "ring-2 ring-blue-500 ring-inset" : "")} data-testid={"day-" + day}>
                    <span className={"text-sm font-medium " + (isToday ? "text-blue-600" : "")}>{day}</span>
                    <div className="mt-1 space-y-0.5">
                      {dayEvents.map(function(ev) {
                        return (
                          <div key={ev.event_id} className={"text-xs px-1 py-0.5 rounded truncate cursor-pointer " + (typeColors[ev.event_type] || "bg-slate-100")} title={ev.title} data-testid={"event-" + ev.event_id}>
                            {ev.time ? ev.time + " " : ""}{ev.title}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Upcoming events list */}
        <div className="mt-6">
          <h2 className="text-base font-semibold mb-3">Événements à venir</h2>
          <div className="space-y-2">
            {events.length === 0 ? (
              <p className="text-sm text-muted-foreground">Aucun événement ce mois-ci</p>
            ) : events.map(function(ev) {
              return (
                <div key={ev.event_id} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-white" data-testid={"upcoming-" + ev.event_id}>
                  <Badge className={typeColors[ev.event_type] + " text-xs"}>{typeLabels[ev.event_type]}</Badge>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{ev.title}</p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span>{ev.date}</span>{ev.time && <span>{ev.time}</span>}{ev.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{ev.location}</span>}
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={function() { handleDelete(ev.event_id); }}><Trash2 className="w-4 h-4" /></Button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
